<script setup lang="ts">
  import { Button, Icon, IconName, IconSize, Picture, Typo, TypoVariant, PictureFormat } from '@travelata-libs/ui-kit';
  import type { AppDownloadStore } from '@/shared/model';
  import MobilePageHeader from '@/shared/ui/MobilePageHeader.vue';
  import MobileScreen from '@/shared/ui/MobileScreen.vue';
  import { APP_PROMO_TEXTS } from '../config/texts';

  interface MobileAccountScreenProps {
    embedded?: boolean;
    accountUrl?: string;
    onBack: () => void;
    onChat: () => void;
    onDownload: (store: AppDownloadStore | null) => void;
  }

  const props = withDefaults(defineProps<MobileAccountScreenProps>(), {
    embedded: false,
    accountUrl: '',
  });

  const onDownload = (): void => {
    props.onDownload(null);
  };

  const onBrowser = (): void => {
    if (typeof window !== 'undefined' && props.accountUrl) {
      window.location.href = props.accountUrl;
    }
  };
</script>

<template>
  <MobileScreen
    name="mobile-account-screen"
    testid="search-form-mobile-account"
    :embedded="embedded"
  >
    <MobilePageHeader
      class="mobile-account-screen__header"
      :title="APP_PROMO_TEXTS.title"
      :title-variant="TypoVariant.Heading6"
      @back="onBack"
    >
      <template #action>
        <Icon
          class="mobile-account-screen__chat"
          :name="IconName.MessageDots"
          :size="IconSize.L"
          :aria-label="APP_PROMO_TEXTS.chatAriaLabel"
          data-testid="mobile-account-chat"
          @click.native="onChat"
        />
      </template>
    </MobilePageHeader>

    <div class="mobile-account-screen__body">
      <Picture
        path="illustrations/castle.webp"
        :high-resolution="true"
        :lazy="false"
        :extensions="[PictureFormat.WEBP]"
        class="mobile-account-screen__picture"
      />
      <div class="mobile-account-screen__texts">
        <Typo
          class="mobile-account-screen__heading"
          :variant="TypoVariant.Heading5"
          tag="h1"
        >
          {{ APP_PROMO_TEXTS.heading }}
        </Typo>
        <Typo
          class="mobile-account-screen__subtitle"
          :variant="TypoVariant.BodyLRegular"
          tag="p"
        >
          {{ APP_PROMO_TEXTS.subtitle }}
        </Typo>
      </div>
    </div>

    <div class="mobile-account-screen__footer">
      <Button
        class="mobile-account-screen__button"
        appearance="primary"
        data-testid="mobile-account-download"
        size="l"
        @click="onDownload"
      >
        {{ APP_PROMO_TEXTS.download }}
      </Button>
      <Button
        class="mobile-account-screen__button"
        appearance="tertiary"
        data-testid="mobile-account-browser"
        size="l"
        icon-left="device-desktop"
        @click="onBrowser"
      >
        {{ APP_PROMO_TEXTS.browser }}
      </Button>
    </div>
  </MobileScreen>
</template>

<style scoped lang="scss">
  .mobile-account-screen {
    background: var(--color-bg-primary);

    &__header {
      box-sizing: border-box;
      flex: 0 0 auto;
      padding: var(--spacing-s) var(--spacing-m) var(--spacing-xs) var(--spacing-m);
    }

    &__chat {
      cursor: pointer;
      fill: var(--color-text-primary);
    }

    &__picture {
      height: 221px;
      width: 221px;
    }

    &__body {
      align-items: center;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: var(--spacing-l) var(--spacing-l) 0;
    }

    &__texts {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      text-align: center;
    }

    &__heading {
      color: var(--color-text-primary);
      white-space: pre-line;
    }

    &__subtitle {
      color: var(--color-text-primary);
      white-space: pre-line;
    }

    &__footer {
      box-sizing: border-box;
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      gap: var(--spacing-s);
      padding: var(--spacing-l) var(--spacing-m) var(--spacing-xl);
    }

    &__button {
      width: 100%;
    }
  }
</style>
